import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMeOrNull } from "@/lib/auth";
import { fetchLanguages, fetchProfile, keys, updateProfile } from "@/lib/queries";
import { DEFAULT_LOCALE, getActiveLocale, hasStoredLocale, setActiveLocale } from "@/lib/i18n";
import type { LanguageItem } from "@/lib/types";

// LanguageProvider — the app-level owner of the active UI/catalog language
// (SM-286). lib/i18n owns the mechanics (i18next + localStorage + <html
// lang>); this provider owns the React-facing policy:
//
//   Boot order: localStorage "sm_lang" (an explicit device choice, applied
//   before first render by lib/i18n) → the signed-in profile's locale (below,
//   only when the device carries no explicit choice) → "en".
//
//   On switch: swap i18next + persistence, then invalidate the whole
//   `keys.markets` catalog family — catalog content is localized server-side,
//   so every active catalog query refetches with the new `?locale=` and the
//   visible names swap in place. Signed-in switches also PATCH
//   /api/me/profile {locale} so the choice follows the account.
//
// The enabled-language list comes from GET /api/languages (public, ~5 min
// stale) — a language registered at runtime (MCP languages admin) shows up
// here with no client release; its chrome falls back to en while catalog
// content still localizes.
interface LanguageContextValue {
  locale: string;
  setLocale: (code: string) => void;
  languages: LanguageItem[];
}

// Mirrors the migration-seeded registry rows — the selector's offline/initial
// fallback so the picker is never empty while /api/languages loads (or when
// the endpoint is unreachable). Server data replaces this as soon as it lands.
const FALLBACK_LANGUAGES: LanguageItem[] = [
  { code: "en", name: "English", native_name: "English", display_order: 0 },
  { code: "th", name: "Thai", native_name: "ไทย", display_order: 1 },
  { code: "zh-CN", name: "Simplified Chinese", native_name: "简体中文", display_order: 2 },
  { code: "fil", name: "Filipino", native_name: "Filipino", display_order: 3 },
  { code: "vi", name: "Vietnamese", native_name: "Tiếng Việt", display_order: 4 },
];

const Ctx = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [locale, setLocaleState] = useState(getActiveLocale());

  const me = useQuery({ queryKey: keys.me(), queryFn: fetchMeOrNull });
  const profile = useQuery({ queryKey: keys.profile(), queryFn: fetchProfile, enabled: !!me.data });
  const languagesQ = useQuery({
    queryKey: keys.languages(),
    queryFn: fetchLanguages,
    staleTime: 5 * 60_000,
  });

  // Persist a signed-in switch to the profile (fire-and-forget — the local
  // switch already happened; a failed PATCH just means the account copy lags).
  const saveLocale = useMutation({
    mutationFn: (code: string) => updateProfile({ locale: code }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.profile() });
    },
  });

  // applyLocale — shared switch path: i18next/persistence first, then state
  // (re-renders context consumers), then the catalog refetch sweep.
  const applyLocale = useCallback(
    async (code: string) => {
      await setActiveLocale(code);
      setLocaleState(code);
      void qc.invalidateQueries({ queryKey: keys.markets });
    },
    [qc],
  );

  const signedIn = !!me.data;
  const setLocale = useCallback(
    (code: string) => {
      if (code === getActiveLocale()) return;
      void applyLocale(code).then(() => {
        if (signedIn) saveLocale.mutate(code);
      });
    },
    [applyLocale, signedIn, saveLocale],
  );

  // Boot: adopt the signed-in profile's locale when this device has no
  // explicit choice yet. No PATCH-back (it IS the profile value already) and
  // no localStorage precedence violation — setActiveLocale stores it, so from
  // here on the device choice is explicit.
  const profileLocale = profile.data?.locale;
  useEffect(() => {
    if (!profileLocale || hasStoredLocale() || profileLocale === getActiveLocale()) return;
    void applyLocale(profileLocale);
  }, [profileLocale, applyLocale]);

  // De-register guard (SM-314): an operator can rename a locale code or
  // disable a language, which strands anyone still holding the old code —
  // they'd keep sending a `?locale=` the server no longer resolves and see no
  // active row in the picker. When the fetched registry doesn't contain the
  // active locale, fall back to en. Deliberately quiet: the content simply
  // reads in English, which is the same thing the server would have served.
  // Only runs on a settled list, so a slow/failed fetch can't bounce anyone.
  const registry = languagesQ.data;
  useEffect(() => {
    if (!registry || registry.length === 0) return;
    if (registry.some((l) => l.code === locale)) return;
    void applyLocale(DEFAULT_LOCALE);
  }, [registry, locale, applyLocale]);

  const value = useMemo<LanguageContextValue>(
    () => ({ locale, setLocale, languages: languagesQ.data ?? FALLBACK_LANGUAGES }),
    [locale, setLocale, languagesQ.data],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const c = useContext(Ctx);
  if (!c) throw new Error("useLanguage must be used within <LanguageProvider>");
  return c;
}
